import { motion } from 'framer-motion';
import { Target, Eye, Shield, Award, Users, TrendingUp } from 'lucide-react';
import { certifications } from '@/data';
import { IMAGES } from '@/assets/images';

export default function About() {
  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.PORT_OPERATIONS_5}
            alt="Port Operations"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              About C.L. & H Services
            </h1>
            <p className="text-xl text-muted-foreground">
              Comprehensive logistics, marine, and industrial solutions across the Bahamas since 2015
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  Founded in 2015, C.L. & H Services Limited has grown from a heavy duty truck operator to become one of the Bahamas' most comprehensive logistics and industrial services providers. Our journey reflects our commitment to meeting the evolving needs of businesses across the islands.
                </p>
                <p>
                  Today, we operate four distinct service divisions: Heavy Duty Truck Parts, Procurement Services, Barge Operations, and Waste Removal & Management. This multi-service approach allows us to provide integrated solutions for complex projects, from construction and renewable energy to government and maritime operations.
                </p>

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Mission & Vision</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Driving excellence in logistics and industrial services across the Bahamas
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card rounded-2xl p-8 shadow-lg border border-border"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold">Our Mission</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To provide comprehensive, reliable logistics and industrial services that enable businesses and communities across the Bahamas to thrive. We are committed to operational excellence, safety, and customer satisfaction in every service we deliver.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-card rounded-2xl p-8 shadow-lg border border-border"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Eye className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-2xl font-bold">Our Vision</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To be the Bahamas' most trusted and comprehensive logistics partner, recognized for innovation, reliability, and commitment to sustainable development. We envision a future where seamless inter-island logistics supports economic growth across all Bahamian communities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Safety & Compliance</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Committed to the highest standards of safety and regulatory compliance
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-10 h-10 text-primary" />
                </div>
                <div className="text-4xl font-bold mb-2">99.8%</div>
                <div className="text-muted-foreground">Safety Compliance Rate</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-accent" />
                </div>
                <div className="text-4xl font-bold mb-2">5,000+</div>
                <div className="text-muted-foreground">Safety Training Hours Annually</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-2xl bg-chart-3/10 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-10 h-10 text-chart-3" />
                </div>
                <div className="text-4xl font-bold mb-2">Zero</div>
                <div className="text-muted-foreground">Major Incidents in 2025</div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-card rounded-2xl p-8 shadow-lg border border-border"
            >
              <h3 className="text-2xl font-bold mb-4">Our Safety Commitment</h3>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Safety is our top priority across all operations. We maintain comprehensive safety programs, conduct regular training, and enforce strict protocols for all employees and contractors. Our commitment to safety extends to our clients, ensuring that every project is executed with the highest standards of care and compliance.
              </p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span>Comprehensive safety training programs for all personnel</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span>Regular safety audits and equipment inspections</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span>Full compliance with Bahamian and international safety regulations</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <span>Emergency response protocols and 24/7 safety support</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">Certifications & Licenses</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Fully licensed and certified to operate across all service divisions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card rounded-xl p-6 shadow-lg border border-border hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">{cert.name}</h3>
                <p className="text-sm text-muted-foreground mb-3">{cert.description}</p>
                <p className="text-xs text-muted-foreground font-medium">{cert.issuer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}